import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DateTime, DateTimeUnit } from "luxon";
import { Observable } from "rxjs";
import { CepResponse } from "../app/interfaces/cep";

@Injectable({
  providedIn: "root",
})
export class UtilsService {
  constructor(private http: HttpClient) {}
  apiViaCep = "https://viacep.com.br/ws";

  formatarDataHora(dataIso: string): string {
    const data = new Date(dataIso);
    const dia = data.getUTCDate().toString().padStart(2, "0");
    const mes = (data.getUTCMonth() + 1).toString().padStart(2, "0");
    const ano = data.getUTCFullYear();
    const horas = data.getUTCHours().toString().padStart(2, "0");
    const minutos = data.getUTCMinutes().toString().padStart(2, "0");
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  formatarData(dataIso: string): string {
    const data = new Date(dataIso);
    const dia = data.getUTCDate().toString().padStart(2, "0");
    const mes = (data.getUTCMonth() + 1).toString().padStart(2, "0");
    const ano = data.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  formatarHora(dataIso: string): string {
    const data = new Date(dataIso);
    const dia = data.getUTCDate().toString().padStart(2, "0");
    const mes = (data.getUTCMonth() + 1).toString().padStart(2, "0");
    const ano = data.getUTCFullYear();
    const horas = data.getUTCHours().toString().padStart(2, "0");
    const minutos = data.getUTCMinutes().toString().padStart(2, "0");
    return `${horas}:${minutos}`;
  }

  formataValorEmReais = (valor: number | string) => {
    const numero = parseFloat(String(valor).replace(",", "."));

    if (isNaN(numero)) {
      return "Valor inválido";
    }

    const valorFormatado = numero.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    });

    return valorFormatado;
  };

  consultaCEP(cep: string): Observable<CepResponse> {
    return this.http.get<CepResponse>(`${this.apiViaCep}/${cep}/json`);
  }

  extractHourFromISO(isoString: string): string {
    // Quebrar a string no caractere 'T'
    const timePart = isoString.split("T")[1]; // Pega a parte "13:00:00.000-02:00"

    // Quebrar no caractere ':' para isolar a hora
    const hour = timePart.split(":")[0]; // Pega o valor "13"
    const minutes = timePart.split(":")[1]; // Pega o valor "13"

    return `${hour}:${minutes}`;
  }

  isPast(
    dateString: string,
    unit: DateTimeUnit = "minute", // 'second' | 'minute' | 'hour' | 'day' | ...
  ): boolean {
    const date = DateTime.fromISO(dateString);
    if (!date.isValid) return false;
    const now = DateTime.now().setZone(date.zone);
    return unit ? date.startOf(unit) < now.startOf(unit) : date < now;
  }

  hasDatePassed(
    inputDate: Date | string | number,
    inputStartTime?: string,
  ): boolean {
    const currentDate = new Date();
    const endOfToday = new Date(currentDate);
    // endOfToday.setHours(23, 59, 59, 999);

    // Tratar a data de entrada
    const dateParts = inputDate.toString().split("-");
    const year = Number(dateParts[0]);
    const month = Number(dateParts[1]) - 1; // O mês em JavaScript é baseado em zero
    const day = Number(dateParts[2]);

    const formattedInputDate = new Date(year, month, day);

    // Adicionar o horário, se fornecido
    if (inputStartTime) {
      if (inputStartTime.length > 5) {
        inputStartTime = this.extractHourFromISO(inputStartTime);
      }

      const timeSplit = inputStartTime.split(":");
      const hours = Number(timeSplit[0]);
      const minutes = Number(timeSplit[1]);
      formattedInputDate.setHours(hours, minutes, 0, 0);
    }

    return formattedInputDate.getTime() <= endOfToday.getTime();
  }

  toCents(input: number) {
    if (input == null) throw new TypeError("Valor ausente");

    // Normaliza para string sem moeda/espaços
    let s = String(input)
      .trim()
      .replace(/[R$\s]/g, "");
    let negative = false;

    if (s.startsWith("-")) {
      negative = true;
      s = s.slice(1);
    }
    if (s === "") throw new TypeError("Valor vazio");

    // Detecta formato e normaliza separador decimal para '.'
    if (s.includes(".") && s.includes(",")) {
      // Ex.: "1.234,56" -> "1234,56" -> "1234.56"
      s = s.replace(/\./g, "").replace(",", ".");
    } else if (s.includes(",")) {
      // Ex.: "1234,56" -> "1234.56"
      s = s.replace(",", ".");
    }
    // Agora só pode haver um '.' como decimal (se houver)
    const parts = s.split(".");
    if (
      parts.length > 2 ||
      !/^\d+$/.test(parts[0] || "0") ||
      (parts[1] && !/^\d+$/.test(parts[1]))
    ) {
      throw new TypeError("Valor inválido");
    }

    const intPart = parts[0] || "0";
    const fracRaw = parts[1] || "";

    // Arredonda para 2 casas: pega 3ª casa para decidir arredondamento
    const fracPadded = fracRaw + "000"; // garante pelo menos 3 dígitos
    const frac2 = fracPadded.slice(0, 2); // duas casas
    const third = fracPadded.charCodeAt(2) - 48; // 0..9

    let cents = BigInt(intPart) * 100n + BigInt(frac2 || "0");
    if (third >= 5) cents += 1n; // arredonda

    if (negative) cents = -cents;

    // Retorna como Number (seguro para valores comuns). Use String(cents) se precisar evitar overflow.
    const n = Number(cents);
    if (!Number.isSafeInteger(n))
      throw new RangeError("Valor muito grande para Number");
    return n;
  }
}
